<script lang="ts" setup>
import { FilterType } from '@/coreDomainsModule/entities/filter';
import { DateService } from '@/coreDomainsModule/services/dateService';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import SelectInput from '@/corePagesModule/SelectInput/SelectInput.vue';
import MultipleUserSelectInput from '@/corePagesModule/user/SelectInput/MultipleUserSelectInput.vue';
import { MIN_OF_DISPLAY_FORECAST_MONTH, MAX_OF_DISPLAY_FORECAST_MONTH } from '@/customerForecastModule/constants';
import { ForecastForReceiptFormState } from '@/customerForecastModule/domains/entities/ForecastForReceiptMenu';
import { i18n } from '@/i18n';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  modalState: boolean;
  selectInputIndex: number;
}>();

const emits = defineEmits<{
  (e: 'close'): void;
  (e: 'apply', newForecastForReceiptInfo: ForecastForReceiptFormState): void;
}>();

//変数
const t = i18n.t;

const currentFilterType = ref<FilterType | undefined>(undefined);

const rangeSelectInputData = ref<string[]>(DateService.getMonthRangeList(MIN_OF_DISPLAY_FORECAST_MONTH, MAX_OF_DISPLAY_FORECAST_MONTH));

// 作成を行う受領用内示情報
const newForecastForReceiptInfo = ref<ForecastForReceiptFormState>({
  forecastForReceiptName: '',
  forecastForReceiptResponsibleUserIdList: [],
  outputRange: MIN_OF_DISPLAY_FORECAST_MONTH,
});

watch(
  () => props.modalState,
  (newVal) => {
    console.log('newVal', newVal);
    if (newVal) {
      newForecastForReceiptInfo.value.forecastForReceiptName = '';
    }
  }
);

watch(
  () => i18n.locale,
  () => {
    rangeSelectInputData.value = DateService.getMonthRangeList(MIN_OF_DISPLAY_FORECAST_MONTH, MAX_OF_DISPLAY_FORECAST_MONTH);
  }
);

const isDisabledApplyButton = computed(() => {
  return (
    newForecastForReceiptInfo.value.forecastForReceiptName == null ||
    newForecastForReceiptInfo.value.forecastForReceiptName === '' ||
    newForecastForReceiptInfo.value.outputRange == null ||
    (newForecastForReceiptInfo.value.outputRange <= MIN_OF_DISPLAY_FORECAST_MONTH &&
      newForecastForReceiptInfo.value.outputRange > MAX_OF_DISPLAY_FORECAST_MONTH) // この条件は一応で入れてる
  );
});

/**
 * 範囲選択のセレクトインプットで選択された値を取得する
 * @param index - 選択された値のインデックス
 */
const onSelectedRangeValue = (index: number) => {
  const selectedRange = rangeSelectInputData.value[index];
  // 文字列から数字のみを抽出
  const rangeValue = selectedRange.replace(/[^0-9]/g, '');
  newForecastForReceiptInfo.value.outputRange = Number(rangeValue);
};
</script>

<template>
  <InfoModal
    :modal-state="modalState"
    :back-button-label="t('キャンセル')"
    :next-button-label="t('新規作成')"
    :modal-width="764"
    :is-disabled-next-button="isDisabledApplyButton"
    @on-back="emits('close')"
    @on-next="emits('apply', newForecastForReceiptInfo)"
  >
    <template #title>{{ t('受領用内示の新規作成') }}</template>
    <template #description>{{ t('受領用内示情報を新規作成します。') }}</template>

    <div class="create-forecast-for-receipt-modal-content__main-content main-content">
      <div class="main-content__container">
        <div class="main-content__title">
          {{ t('受領用内示を新規作成') }}
        </div>
        <input v-model="newForecastForReceiptInfo.forecastForReceiptName" type="text" class="main-content__input" />
        <MultipleUserSelectInput
          :select-input-index="selectInputIndex"
          :original-selected-user-id-list="newForecastForReceiptInfo.forecastForReceiptResponsibleUserIdList"
          @update:model-value="newForecastForReceiptInfo.forecastForReceiptResponsibleUserIdList = $event"
        />
        <div class="main-content__select-output-range-picker--container select-output-range-picker__container">
          <!-- 終了年月の選択はセレクトインプットから選択式にする -->
          <SelectInput
            class="select-output-range-picker__end-month"
            :index="selectInputIndex + 2"
            :model-value="rangeSelectInputData[0]"
            :placeholder="''"
            :label="t('出力する月数')"
            :filter-type="'DISPLAY_YEAR_MONTH'"
            :use-reset="false"
            :prop-options="rangeSelectInputData"
            :is-open-prop="currentFilterType === 'DISPLAY_YEAR_MONTH'"
            :use-search-input="false"
            @click:select-input="() => (currentFilterType = 'DISPLAY_YEAR_MONTH')"
            @update:value-with-index="(_, index) => onSelectedRangeValue(index)"
          />
        </div>
      </div>
    </div>
  </InfoModal>
</template>

<style lang="scss" scoped>
.create-forecast-for-receipt-modal-content {
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 24px;
}

.main-content {
  background-color: $backgroundDefault;
  border-radius: 8px;

  &__container {
    height: 100%;
    width: 100%;
    padding: 16px;
    display: grid;
    grid-template-rows: repeat(3, auto);
    gap: 8px;
  }

  &__title {
    font-size: 14px;
    font-weight: 400;
    color: $textDefault;
  }

  &__select-input {
    width: 100%;
    box-sizing: border-box;
    font-size: 14px;
    font-weight: 400;
    color: $textDefault;
  }

  &__input {
    width: 100%;
    box-sizing: border-box;
    background-color: $semanticWhite;
    border-radius: 4px;
    border: 1px solid $borderDefault;
    font-size: 14px;
    font-weight: 400;
    color: $textDefault;
    padding: 7px 8px;
  }
}

.select-output-range-picker {
  width: 100%;
  display: grid;
  grid-template-rows: repeat(2, auto);
  gap: 16px;
  background-color: $backgroundDefault;
  padding: 16px;
  border-radius: 8px;

  &__container {
    display: grid;
    align-items: center;
  }

  &__end-month {
    width: 100%;
    box-sizing: border-box;
    font-size: 14px;
    font-weight: 400;
    color: $textDefault;
  }
}
</style>
